import React from 'react';
import { Download } from 'lucide-react';
import { Button } from './ui/button';
import { SEO, SEOConfig } from './SEO';
import { Footer } from './Footer';
import { Header } from './Header';

const AVVPage = () => {
  return (
    <>
      <SEO {...SEOConfig.avv} />
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50">
        <Header />

        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="bg-white rounded-2xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-8 py-6">
              <h1 className="text-3xl font-bold text-white">
                Auftragsverarbeitungsvereinbarung (AVV)
              </h1>
              <p className="text-blue-100 mt-2">
                gemäß Art. 28 DSGVO für die Nutzung von IDA
              </p>
            </div>

            {/* Content */}
            <div className="px-8 py-8">
              <div className="prose prose-lg max-w-none">

                <div className="bg-blue-50 border-l-4 border-blue-400 p-4 mb-6">
                  <p className="text-sm text-blue-800">
                    <strong>Stand:</strong> {new Date().toLocaleDateString('de-DE')}
                  </p>
                  <p className="text-sm text-blue-800 mt-2">
                    Diese AVV wird automatisch Bestandteil des Nutzungsvertrags bei Vertragsabschluss.
                  </p>
                </div>

                {/* Präambel */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-100 pb-2">
                    Präambel
                  </h2>
                  <div className="space-y-4 text-gray-700">
                    <p>Diese Vereinbarung regelt die Auftragsverarbeitung personenbezogener Daten gemäß Art. 28 DSGVO zwischen:</p>
                    <p><strong>Verantwortlicher (Auftraggeber):</strong> Der Kunde/Nutzer der IDA-Plattform</p>
                    <p><strong>Auftragsverarbeiter (Auftragnehmer):</strong> EnBet-Consulting UG (haftungsbeschränkt), Im Tal 30, 34270 Schauenburg</p>
                  </div>
                </section>

                {/* 1. Gegenstand und Dauer */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-100 pb-2">
                    1. Gegenstand und Dauer
                  </h2>
                  <div className="space-y-4 text-gray-700">
                    <p><strong>1.1 Gegenstand:</strong> Bereitstellung der SaaS-Plattform IDA zur Compliance-Dokumentation, einschließlich Speicherung und Verarbeitung der vom Kunden eingegebenen Daten.</p>
                    <p><strong>1.2 Dauer:</strong> Die AVV gilt für die Dauer des Hauptvertrags (Nutzungsvertrag für IDA).</p>
                    <p><strong>1.3 Art der Daten:</strong> Mitarbeiterdaten, Dokumentationen, Checklisten, Audit-Logs, ggf. Gästedaten (Responsible Gaming)</p>
                    <p><strong>1.4 Kategorien Betroffener:</strong> Mitarbeiter des Kunden, ggf. Gäste/Kunden des Auftraggebers</p>
                  </div>
                </section>

                {/* 2. Weisungen */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-100 pb-2">
                    2. Weisungen des Auftraggebers
                  </h2>
                  <div className="space-y-4 text-gray-700">
                    <p><strong>2.1</strong> Der Auftragnehmer verarbeitet personenbezogene Daten ausschließlich auf dokumentierte Weisung des Auftraggebers.</p>
                    <p><strong>2.2</strong> Die Weisung erfolgt durch die Nutzung der IDA-Plattform (Dateneingabe, -export, -löschung).</p>
                    <p><strong>2.3</strong> Der Auftragnehmer informiert den Auftraggeber unverzüglich, wenn eine Weisung gegen Datenschutzrecht verstößt.</p>
                  </div>
                </section>

                {/* 3. Vertraulichkeit */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-100 pb-2">
                    3. Vertraulichkeit
                  </h2>
                  <div className="space-y-4 text-gray-700">
                    <p><strong>3.1</strong> Alle mit der Datenverarbeitung betrauten Personen sind zur Vertraulichkeit verpflichtet.</p>
                    <p><strong>3.2</strong> Zugriff auf personenbezogene Daten haben nur autorisierte Mitarbeiter (Need-to-know-Prinzip).</p>
                  </div>
                </section>

                {/* 4. Technische und organisatorische Maßnahmen (TOM) */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-100 pb-2">
                    4. Technische und organisatorische Maßnahmen (TOM)
                  </h2>
                  <div className="space-y-4 text-gray-700">
                    <p>Der Auftragnehmer trifft folgende Maßnahmen gem. Art. 32 DSGVO:</p>

                    <h4 className="font-semibold text-gray-800 mt-4">Vertraulichkeit (Art. 32 Abs. 1 lit. b DSGVO)</h4>
                    <ul className="list-disc list-inside ml-4">
                      <li>Zugriffskontrolle: API-Key-Authentifizierung, Rollenbasierte Zugriffsrechte</li>
                      <li>Verschlüsselung: TLS 1.3 für Datenübertragung, verschlüsselte Datenbank-Backups</li>
                      <li>Passwort-Sicherheit: Bcrypt-Hashing für Passwörter</li>
                    </ul>

                    <h4 className="font-semibold text-gray-800 mt-4">Integrität (Art. 32 Abs. 1 lit. b DSGVO)</h4>
                    <ul className="list-disc list-inside ml-4">
                      <li>Weitergabekontrolle: Protokollierung aller Zugriffe (Audit-Logs)</li>
                      <li>Eingabekontrolle: Nachvollziehbarkeit aller Änderungen</li>
                    </ul>

                    <h4 className="font-semibold text-gray-800 mt-4">Verfügbarkeit und Belastbarkeit (Art. 32 Abs. 1 lit. b DSGVO)</h4>
                    <ul className="list-disc list-inside ml-4">
                      <li>Backup: Tägliche automatische Backups mit 30-Tage-Aufbewahrung</li>
                      <li>Verfügbarkeit: 99,5% Uptime-Garantie (siehe SLA)</li>
                      <li>ISO 27001-zertifiziertes Rechenzentrum (Hetzner AG)</li>
                    </ul>

                    <h4 className="font-semibold text-gray-800 mt-4">Verfahren zur regelmäßigen Überprüfung (Art. 32 Abs. 1 lit. d DSGVO)</h4>
                    <ul className="list-disc list-inside ml-4">
                      <li>Regelmäßige Sicherheitsupdates</li>
                      <li>Jährliche Überprüfung der TOM</li>
                      <li>Incident-Response-Plan</li>
                    </ul>
                  </div>
                </section>

                {/* 5. Unterauftragsverarbeiter */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-100 pb-2">
                    5. Unterauftragsverarbeiter
                  </h2>
                  <div className="space-y-4 text-gray-700">
                    <p><strong>5.1</strong> Der Auftraggeber stimmt der Beauftragung folgender Unterauftragsverarbeiter zu:</p>

                    <div className="bg-gray-50 p-4 rounded-lg mt-4">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="border-b border-gray-300">
                            <th className="text-left py-2">Unternehmen</th>
                            <th className="text-left py-2">Leistung</th>
                            <th className="text-left py-2">Standort</th>
                          </tr>
                        </thead>
                        <tbody>
                          <tr className="border-b border-gray-200">
                            <td className="py-2">Hetzner Online GmbH</td>
                            <td className="py-2">Server-Hosting</td>
                            <td className="py-2">Deutschland</td>
                          </tr>
                          <tr className="border-b border-gray-200">
                            <td className="py-2">bergx2.de</td>
                            <td className="py-2">E-Mail-Versand (SMTP)</td>
                            <td className="py-2">Deutschland</td>
                          </tr>
                        </tbody>
                      </table>
                    </div>

                    <p><strong>5.2</strong> Bei Beauftragung neuer Unterauftragsverarbeiter wird der Auftraggeber mindestens 30 Tage im Voraus informiert und kann widersprechen.</p>
                    <p><strong>5.3</strong> Mit allen Unterauftragsverarbeitern bestehen DSGVO-konforme Verträge.</p>
                  </div>
                </section>

                {/* 6. Betroffenenrechte */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-100 pb-2">
                    6. Unterstützung bei Betroffenenrechten
                  </h2>
                  <div className="space-y-4 text-gray-700">
                    <p><strong>6.1</strong> Der Auftragnehmer unterstützt den Auftraggeber mit geeigneten technischen und organisatorischen Maßnahmen bei der Erfüllung von Betroffenenrechten (Auskunft, Löschung, Berichtigung, etc.).</p>
                    <p><strong>6.2</strong> Export-Funktion für Datenübertragbarkeit (Art. 20 DSGVO) ist in der Plattform integriert (CSV/PDF-Export).</p>
                    <p><strong>6.3</strong> Löschfunktion für Daten ist verfügbar; Aufbewahrungspflichten bleiben unberührt.</p>
                  </div>
                </section>

                {/* 7. Datenlöschung und -rückgabe */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-100 pb-2">
                    7. Datenlöschung und -rückgabe nach Vertragsende
                  </h2>
                  <div className="space-y-4 text-gray-700">
                    <p><strong>7.1</strong> Nach Beendigung des Vertrags stellt der Auftragnehmer alle Daten innerhalb von 30 Tagen zur Verfügung:</p>
                    <ul className="list-disc list-inside ml-4">
                      <li>Self-Service-Export über die Plattform (CSV/PDF)</li>
                      <li>Auf Wunsch: Datenbankdump als SQL/JSON</li>
                    </ul>
                    <p><strong>7.2</strong> Nach erfolgter Rückgabe werden alle Daten gelöscht, sofern keine gesetzlichen Aufbewahrungspflichten bestehen.</p>
                    <p><strong>7.3</strong> Backups werden nach 30 Tagen automatisch überschrieben.</p>
                  </div>
                </section>

                {/* 8. Kontroll- und Prüfrechte */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-100 pb-2">
                    8. Kontroll- und Prüfrechte
                  </h2>
                  <div className="space-y-4 text-gray-700">
                    <p><strong>8.1</strong> Der Auftraggeber kann die Einhaltung der Datenschutzbestimmungen überprüfen durch:</p>
                    <ul className="list-disc list-inside ml-4">
                      <li>Einholung von Auskünften</li>
                      <li>Einsicht in relevante Aufzeichnungen (Audit-Logs)</li>
                      <li>Vor-Ort-Inspektionen (nach vorheriger Ankündigung, max. 1x jährlich)</li>
                    </ul>
                    <p><strong>8.2</strong> ISO 27001-Zertifikat des Hosting-Partners (Hetzner) wird auf Anfrage bereitgestellt.</p>
                  </div>
                </section>

                {/* 9. Meldung von Datenschutzverletzungen */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-100 pb-2">
                    9. Datenschutzverletzungen (Data Breach)
                  </h2>
                  <div className="space-y-4 text-gray-700">
                    <p><strong>9.1</strong> Der Auftragnehmer meldet Datenschutzverletzungen unverzüglich (binnen 24 Stunden) an den Auftraggeber.</p>
                    <p><strong>9.2</strong> Die Meldung enthält mindestens:</p>
                    <ul className="list-disc list-inside ml-4">
                      <li>Art der Verletzung</li>
                      <li>Betroffene Datenkategorien und Anzahl Betroffener (soweit möglich)</li>
                      <li>Wahrscheinliche Folgen</li>
                      <li>Ergriffene/geplante Maßnahmen</li>
                    </ul>
                    <p><strong>9.3</strong> Die Meldung an Aufsichtsbehörden (Art. 33 DSGVO) obliegt dem Auftraggeber als Verantwortlichem.</p>
                  </div>
                </section>

                {/* 10. Datenschutz-Folgenabschätzung */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-100 pb-2">
                    10. Datenschutz-Folgenabschätzung
                  </h2>
                  <div className="space-y-4 text-gray-700">
                    <p>Der Auftragnehmer unterstützt den Auftraggeber auf Anfrage bei der Durchführung einer Datenschutz-Folgenabschätzung (Art. 35 DSGVO), sofern diese erforderlich ist.</p>
                  </div>
                </section>

                {/* 11. Haftung und Gewährleistung */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-100 pb-2">
                    11. Haftung
                  </h2>
                  <div className="space-y-4 text-gray-700">
                    <p>Die Haftung richtet sich nach den Bestimmungen des Hauptvertrags (AGB). Der Auftragnehmer haftet für Verstöße gegen datenschutzrechtliche Pflichten nach Maßgabe der gesetzlichen Bestimmungen (Art. 82 DSGVO).</p>
                  </div>
                </section>

                {/* 12. Schlussbestimmungen */}
                <section className="mb-8">
                  <h2 className="text-2xl font-bold text-gray-900 mb-4 border-b-2 border-blue-100 pb-2">
                    12. Schlussbestimmungen
                  </h2>
                  <div className="space-y-4 text-gray-700">
                    <p><strong>12.1</strong> Änderungen dieser AVV bedürfen der Textform (E-Mail genügt).</p>
                    <p><strong>12.2</strong> Sollten einzelne Bestimmungen unwirksam sein, bleibt die Wirksamkeit der übrigen Bestimmungen unberührt.</p>
                    <p><strong>12.3</strong> Diese AVV wird automatisch Bestandteil des Nutzungsvertrags. Eine separate Unterzeichnung ist nicht erforderlich.</p>
                  </div>
                </section>

                {/* Anlagen */}
                <div className="bg-gray-50 border border-gray-300 rounded-lg p-6 mt-8">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Anlagen zu dieser AVV</h3>
                  <ul className="space-y-2 text-gray-700">
                    <li>
                      <strong>Anlage 1:</strong> Technische und Organisatorische Maßnahmen (TOM) – siehe Abschnitt 4
                    </li>
                    <li>
                      <strong>Anlage 2:</strong> Liste der Unterauftragsverarbeiter – siehe Abschnitt 5
                    </li>
                    <li>
                      <strong>Anlage 3:</strong> Datenlösch- und Rückgabekonzept – siehe Abschnitt 7
                    </li>
                  </ul>
                </div>

                {/* Download */}
                <div className="mt-8 text-center">
                  <Button
                    className="bg-blue-600 hover:bg-blue-700 text-white"
                    onClick={() => window.print()}
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Als PDF speichern/drucken
                  </Button>
                  <p className="text-sm text-gray-500 mt-2">
                    Verwenden Sie die Druckfunktion Ihres Browsers, um diese AVV als PDF zu speichern.
                  </p>
                </div>

              </div>
            </div>
          </div>
        </main>

        <Footer className="bg-gray-900 text-white py-8 mt-12" />
      </div>
    </>
  );
};

export default AVVPage;
